import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const Parrot = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <capsuleGeometry args={[0.1, 0.15, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FF4500' : '#9932CC'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.45, 0.05]} castShadow>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#FFD700' : '#DDA0DD'} />
      </mesh>
      {/* Beak */}
      <mesh position={[0, 0.42, 0.12]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.03, 0.08, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#2F2F2F' : '#4a4a5a'} />
      </mesh>
      {/* Wings */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.1, 0.25, -0.02]} rotation={[0, 0, side * 0.3]}>
          <planeGeometry args={[0.15, 0.12]} />
          <meshStandardMaterial
            color={world === 'real' ? '#4169E1' : '#8A2BE2'}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* Tail feathers */}
      <mesh position={[0, 0.15, -0.12]} rotation={[0.5, 0, 0]}>
        <coneGeometry args={[0.05, 0.2, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#32CD32' : '#9370DB'} />
      </mesh>
      {/* Eyes */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.47, 0.1]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
    </group>
  );
};

const Monkey = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <capsuleGeometry args={[0.12, 0.2, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a3a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.45, 0.05]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#A0522D' : '#6a4a3a'} />
      </mesh>
      {/* Face */}
      <mesh position={[0, 0.43, 0.12]}>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#DEB887' : '#b0a090'} />
      </mesh>
      {/* Ears */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.48, 0]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#A0522D' : '#6a4a3a'} />
        </mesh>
      ))}
      {/* Arms */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.15, 0.2, 0]} rotation={[0, 0, side * 0.5]}>
          <capsuleGeometry args={[0.03, 0.15, 4, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3a2a'} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.15, -0.15]} rotation={[1, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.015, 0.3, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3a2a'} />
      </mesh>
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.45, 0.16]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
    </group>
  );
};

const Toucan = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.12, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#000000' : '#2a2a3a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.35, 0.03]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#000000' : '#2a2a3a'} />
      </mesh>
      {/* Giant beak */}
      <mesh position={[0, 0.32, 0.15]} rotation={[0.2, 0, 0]}>
        <capsuleGeometry args={[0.04, 0.12, 4, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFA500' : '#b07030'} />
      </mesh>
      {/* Beak tip */}
      <mesh position={[0, 0.3, 0.25]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FF4500' : '#a04020'} />
      </mesh>
      {/* Chest */}
      <mesh position={[0, 0.25, 0.06]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFF00' : '#b0b030'} />
      </mesh>
      {/* Eyes */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.37, 0.07]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#FFFFFF" />
        </mesh>
      ))}
    </group>
  );
};

const Jaguar = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body - lying down */}
      <mesh position={[0, 0.12, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[0.25, 0.15, 0.4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#DAA520' : '#8a6a30'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Spots */}
      {[[0.08, 0.18, 0.1], [-0.08, 0.18, -0.05], [0.05, 0.18, -0.12], [-0.06, 0.18, 0.08]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <circleGeometry args={[0.03, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#000000' : '#1a1a1a'} />
        </mesh>
      ))}
      {/* Head */}
      <mesh position={[0, 0.18, 0.22]} castShadow>
        <boxGeometry args={[0.15, 0.12, 0.12]} />
        <meshStandardMaterial color={world === 'real' ? '#DAA520' : '#8a6a30'} />
      </mesh>
      {/* Ears */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.26, 0.2]}>
          <coneGeometry args={[0.025, 0.04, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#DAA520' : '#8a6a30'} />
        </mesh>
      ))}
      {/* Muzzle */}
      <mesh position={[0, 0.15, 0.3]}>
        <boxGeometry args={[0.08, 0.06, 0.06]} />
        <meshStandardMaterial color={world === 'real' ? '#FFF8DC' : '#c0b8a0'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.16, 0.33]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.2, 0.27]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#7CFC00" />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.1, -0.25]} rotation={[0.3, 0, 0]}>
        <cylinderGeometry args={[0.025, 0.02, 0.25, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#DAA520' : '#8a6a30'} />
      </mesh>
      {/* Front paws */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.05, 0.15]}>
          <boxGeometry args={[0.06, 0.05, 0.08]} />
          <meshStandardMaterial color={world === 'real' ? '#DAA520' : '#8a6a30'} />
        </mesh>
      ))}
    </group>
  );
};

const Butterfly = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <capsuleGeometry args={[0.015, 0.06, 4, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2F2F2F' : '#3a3a4a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Wings */}
      {[-1, 1].map((side, i) => (
        <group key={i}>
          {/* Upper wing */}
          <mesh position={[side * 0.08, 0.32, 0]} rotation={[0.1, side * 0.2, side * 0.3]}>
            <planeGeometry args={[0.12, 0.1]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FF6B35' : '#9932CC'}
              emissive={world === 'spectral' ? '#9932CC' : undefined}
              emissiveIntensity={0.3}
              side={THREE.DoubleSide}
              transparent
              opacity={0.9}
            />
          </mesh>
          {/* Lower wing */}
          <mesh position={[side * 0.06, 0.28, 0]} rotation={[0.1, side * 0.1, side * 0.5]}>
            <planeGeometry args={[0.08, 0.08]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFD700' : '#DDA0DD'}
              emissive={world === 'spectral' ? '#DDA0DD' : undefined}
              emissiveIntensity={0.2}
              side={THREE.DoubleSide}
              transparent
              opacity={0.9}
            />
          </mesh>
        </group>
      ))}
      {/* Antennae */}
      {[-0.015, 0.015].map((x, i) => (
        <mesh key={i} position={[x, 0.36, 0.01]} rotation={[0.3, 0, (i - 0.5) * 0.4]}>
          <cylinderGeometry args={[0.003, 0.002, 0.05, 4]} />
          <meshStandardMaterial color="#2F2F2F" />
        </mesh>
      ))}
    </group>
  );
};

export const jungleAnimals: NPCModelRegistry = {
  parrot: Parrot,
  monkey: Monkey,
  toucan: Toucan,
  jaguar: Jaguar,
  butterfly: Butterfly,
};
